/**
 * Which document an auto-save belongs to, and which row it writes.
 *
 * The editor's auto-save runs in SESSIONS (see useAutoSave's `flowKey`): one
 * session per document on screen. Edits of a left session are flushed with
 * that session's own save closure and never written into the next document.
 * This module decides where the session boundaries are and which row id a
 * save uses. Pure bookkeeping, no React, so every rule below is unit-tested.
 *
 *  - A different flow id on screen is a new session...
 *  - ...except when the id is the one this session's own save just minted for
 *    an unsaved canvas (null -> minted id): same canvas, same session.
 *  - Replacing the canvas content (import, template load, open another flow,
 *    new canvas) is a new session EVEN IF the id stays null. Otherwise the old
 *    canvas's in-flight first save would mint its row and the imported content
 *    would then overwrite it.
 *  - The id a save minted belongs to its SESSION, whether or not the editor is
 *    still showing it: a flush of a left, unsaved canvas reuses the row its
 *    earlier save created instead of minting a second knod.
 */

/** UUID v4 — the shape flowToRow accepts unchanged. */
function defaultNewId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export interface SaveSession {
  /** Changes whenever a new session starts (useAutoSave's flowKey). */
  readonly key: number;
  /** The flow id the editor showed for this session (null = unsaved canvas). */
  flowId: string | null;
  /** A row id a save of this session created for the unsaved canvas. */
  mintedId: string | null;
}

export class FlowSaveSessions {
  private session: SaveSession;

  constructor(
    initialFlowId: string | null = null,
    private readonly newId: () => string = defaultNewId,
  ) {
    this.session = { key: 0, flowId: initialFlowId, mintedId: null };
  }

  get current(): SaveSession {
    return this.session;
  }

  /**
   * Call on every render with the flow id on screen. Idempotent, so it is safe
   * during render (StrictMode renders twice). Returns the session to use.
   */
  observe(flowId: string | null): SaveSession {
    const s = this.session;
    if (s.flowId === flowId) return s;
    const adoptedOwnId = s.flowId === null && flowId !== null && flowId === s.mintedId;
    if (adoptedOwnId) {
      s.flowId = flowId;
      return s;
    }
    this.session = { key: s.key + 1, flowId, mintedId: null };
    return this.session;
  }

  /**
   * The canvas content is being replaced (import, template, another flow, new
   * canvas). Call in the same synchronous block that sets the new nodes.
   */
  replaceContent(nextFlowId: string | null = null): SaveSession {
    this.session = { key: this.session.key + 1, flowId: nextFlowId, mintedId: null };
    return this.session;
  }

  /** The row a save of `session` writes: the editor's id, else the one it minted. */
  static idForSave(session: SaveSession, renderedFlowId: string | null): string | null {
    return renderedFlowId || session.mintedId;
  }

  /**
   * The row every save of `session` must write — autosave AND manual save.
   * For an unsaved canvas the id is reserved on the session BEFORE the first
   * write, so a second save issued while the first is still on the wire
   * (Ctrl+S during the first autosave) targets the same row instead of
   * creating a second knod.
   */
  reserveId(session: SaveSession, renderedFlowId: string | null): string {
    if (renderedFlowId) return renderedFlowId;
    if (!session.mintedId) session.mintedId = this.newId();
    return session.mintedId;
  }

  /**
   * A save of `session` finished and wrote row `savedId`. Records the id on the
   * session (always — a later flush of this session must reuse it). Returns
   * true when the editor should ADOPT the id into its own state: only for a
   * freshly minted id, and only while this session is still the one on screen.
   */
  recordSaved(session: SaveSession, knownId: string | null, savedId: string, sessionIsCurrent: boolean): boolean {
    if (knownId) return false;
    session.mintedId = savedId;
    return sessionIsCurrent && session === this.session;
  }

  /**
   * One auto-save of `session`, as the editor runs it: pick the row, write,
   * record the row on the session. `write(id)` serialises with that id
   * (null = mint one) and resolves with the id it wrote.
   */
  async runSave(
    session: SaveSession,
    renderedFlowId: string | null,
    isCurrent: () => boolean,
    write: (id: string) => Promise<string>,
  ): Promise<{ id: string; minted: boolean; adopt: boolean }> {
    const minted = !renderedFlowId;
    const id = await write(this.reserveId(session, renderedFlowId));
    const adopt = this.recordSaved(session, renderedFlowId, id, isCurrent());
    return { id, minted, adopt };
  }
}
